import React, { useEffect, useMemo } from "react"
import { inSeasonProper, seasonLabel } from "./plannerQuery"
import { addDays, daysBetween, holidaysOfWeek, mondayOf, seasonRows, seasonWeeks } from "./plannerCalendar"
import { dayLabel, daysLabel, monthShortLabel, moveWarningLabel, plural } from "./plannerLabels"
import { movable } from "./plannerItems"
import { itemTitle } from "./PlannerCard"
import SeasonTimeline from "./SeasonTimeline"
import useChipDrag from "./useChipDrag"

const CHIPS_PER_CELL = 5
const weekName = (week, holidays) => [`Week of ${dayLabel(week)}`, ...holidaysOfWeek(holidays, week).map(({ name }) => name)].join(" · ")
// The month a week belongs to (the month of its Thursday), shown once over its first week.
const monthOfWeek = week => addDays(week, 3).slice(0, 7)

// The Season view (ticket 15 Q1): the season's weeks, grouped by region (Bulgaria by oblast) or by organizer. A tournament dragged to
// another week of its row moves by whole weeks (ticket 15 Q12); another row is refused: dragging changes only the dates. While it is
// dragged, the preview names the warnings it would have in the week under the pointer (ticket 13 place 6, Phase C1w): `moveWarnings`
// reads those of every week of the season when the drag starts, and again if the calendar is read again meanwhile.
export default function SeasonView({ items, query, holidays, today, settings, onOpen, onShowMonth, onMove, onRefuse, moveWarnings }) {
  const weeks = useMemo(() => seasonWeeks(query.season), [query.season])
  const dragControl = useChipDrag((item, origin, target) => {
    if (target.row !== origin.row) return onRefuse(item)
    const delta = daysBetween(origin.week, target.week)
    return delta ? onMove(item, delta) : undefined
  })
  const { drag } = dragControl
  const dragged = drag?.item, grabbedWeek = drag?.origin.week
  const { load } = moveWarnings
  useEffect(() => {
    if (dragged) load(dragged, weeks.map(week => addDays(dragged.days.start, daysBetween(grabbedWeek, week))))
  }, [dragged, grabbedWeek, load, weeks])
  const dropOn = target => (drag?.target && drag.target.row === target.row && drag.target.week === target.week
    ? (target.row === drag.origin.row ? " is-drop" : " is-refused") : "")
  const delta = drag?.target ? daysBetween(drag.origin.week, drag.target.week) : 0
  const to = drag?.target && { start: addDays(drag.item.days.start, delta), end: addDays(drag.item.days.end, delta) }
  const preview = drag?.target && (drag.target.row === drag.origin.row ? `${drag.item.label} to ${daysLabel(to)}` : "Only the dates change by dragging: keep it in its row")
  const shared = { items, query, weeks, holidays, today, settings, onOpen, drag: dragControl, dropOn }
  return <div className="ep-scroll-x">
    {preview && <div className="ep-drag-pill" aria-hidden="true"><i className="bx bx-right-arrow-alt" />{preview}
      {delta !== 0 && drag.target.row === drag.origin.row && <MoveWarningChips answer={moveWarnings.at(drag.item, to.start)} />}</div>}
    {query.group === "organizer" ? <SeasonTimeline {...shared} /> : <RegionGrid {...shared} onShowMonth={onShowMonth} />}
  </div>
}

// The open warnings at the week under the pointer, as the canvas's preview names them, or that there are none; while they are read, and
// when they could not be, it says so: the drop still moves.
const CHIP_TONES = { holiday: " is-holiday", closeToStart: " is-time" }
function MoveWarningChips({ answer }) {
  if (!answer) return null
  if (answer.status !== "ready") return <span className="ep-pill-chip">{answer.status === "loading" ? "Checking warnings…" : "Warnings could not be checked"}</span>
  const open = answer.warnings.filter(warning => warning.open)
  if (!open.length) return <span className="ep-pill-chip is-ok">No warnings here</span>
  return open.map(warning => <span key={warning.key} className={"ep-pill-chip" + (CHIP_TONES[warning.rule] || " is-warning")}>{moveWarningLabel(warning)}</span>)
}

// Weeks × regions: each tournament a chip in the week it starts, the state's strong fill and the category on its left edge. Weeks with
// a holiday are hatched; months either side of the season are in italics; a fuller cell is darker; "+n" opens that week's month.
function RegionGrid({ items, query, weeks, holidays, today, settings, onOpen, onShowMonth, drag: { drag, press, enter, click }, dropOn }) {
  const rows = useMemo(() => seasonRows(items, weeks, { group: "region", country: query.country }), [items, weeks, query.country])
  const columns = { gridTemplateColumns: `minmax(132px, 168px) repeat(${weeks.length}, minmax(26px, 1fr)) 48px` }
  const weekTotals = weeks.map(week => rows.reduce((sum, row) => sum + row.cells.get(week).length, 0))
  const holidayWeeks = new Set(weeks.filter(week => holidaysOfWeek(holidays, week).length))
  const currentWeek = weeks.find(week => today >= week && today <= addDays(week, 6))

  return <div role="grid" aria-label={`${seasonLabel(query.season)} by week and region`} className={"ep-season-grid" + (drag ? " is-dragging" : "")}>
    <div role="rowgroup">
      <div className="ep-season-row ep-season-months" style={columns} aria-hidden="true">
        <span />
        {weeks.map((week, index) => {
          const month = monthOfWeek(week), first = index === 0 || monthOfWeek(weeks[index - 1]) !== month
          return <span key={week} className={first && !inSeasonProper(query.season, month) ? "is-outside" : ""}>
            {first ? monthShortLabel(`${month}-01`) : ""}</span>
        })}
        <span />
      </div>
      <div role="row" className="ep-season-row ep-season-head" style={columns}>
        <span role="columnheader">{query.country === "Bulgaria" ? "Oblast" : "Region"}</span>
        {weeks.map(week => <span role="columnheader" key={week} aria-label={weekName(week, holidays)} title={weekName(week, holidays)}
          className={"ep-season-week" + (holidayWeeks.has(week) ? " is-holiday" : "") + (week === currentWeek ? " is-today" : "")}>{Number(week.slice(8))}</span>)}
        <span role="columnheader">Total</span>
      </div>
    </div>
    <div role="rowgroup">
      {rows.map(row => <div role="row" key={row.key} className="ep-season-row" style={columns}>
        <span role="rowheader" className="ep-season-label"><b>{row.label}</b><span className="ep-meta">{row.sub}</span></span>
        {weeks.map(week => {
          const cell = row.cells.get(week)
          const density = cell.length >= 6 ? 3 : cell.length >= 3 ? 2 : cell.length ? 1 : 0
          return <div role="gridcell" key={week} aria-label={`${row.label}, week of ${dayLabel(week)}, ${cell.length ? plural(cell.length, "tournament") : "no tournaments"}`}
            className={`ep-season-cell ep-density-${density}` + (holidayWeeks.has(week) ? " is-holiday" : "") + dropOn({ row: row.key, week })}
            onMouseOver={enter({ row: row.key, week })}>
            {cell.slice(0, CHIPS_PER_CELL).map(item => <button type="button" key={item.key} aria-label={itemTitle(item, settings)} title={itemTitle(item, settings)}
              className={`ep-chip-mini ep-fill-${item.state}` + (item.moving ? " is-moving" : "") + (drag?.item.key === item.key ? " is-dragged" : "")
                + (item.needsFees ? " is-no-fees" : "")}
              onMouseDown={movable(item) ? press(item, { row: row.key, week: mondayOf(item.days.start) }) : undefined} onClick={click(() => onOpen(item))}>
              <i className={`ep-cat-edge ep-cat-${item.category}`} aria-hidden="true" /></button>)}
            {cell.length > CHIPS_PER_CELL && <button type="button" className="ep-chip-more" onClick={() => onShowMonth(monthOfWeek(week))}
              aria-label={`${cell.length - CHIPS_PER_CELL} more in the week of ${dayLabel(week)}: open the month`}>+{cell.length - CHIPS_PER_CELL}</button>}
          </div>
        })}
        <span role="gridcell" className="ep-season-total" aria-label={`${row.label}, total ${row.items.length}`}>{row.items.length}</span>
      </div>)}
    </div>
    <div role="rowgroup">
      <div role="row" className="ep-season-row ep-season-foot" style={columns}>
        <span role="rowheader">Total</span>
        {weeks.map((week, index) => <span role="gridcell" key={week}>{weekTotals[index] || ""}</span>)}
        <span role="gridcell">{weekTotals.reduce((sum, count) => sum + count, 0)}</span>
      </div>
    </div>
  </div>
}
